import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ShieldCheck,
  Activity,
  Network,
  Eye,
  Tag,
  ServerCog,
  Gauge,
  Clock,
  AlertTriangle,
  Brain,
  FileCheck2,
  ArrowUpRight,
  Sparkles,
  Cpu,
  LockKeyhole,
  Zap,
  CheckCircle2,
  Quote,
} from "lucide-react";
import SEO from "../components/site/SEO";
import NetworkVisual from "../components/site/NetworkVisual";
import SectionHeader from "../components/site/SectionHeader";

const HERO_BG =
  "https://images.unsplash.com/photo-1751448555253-f39c06e29d82?auto=format&fit=crop&w=1920&q=80";
const VISUAL_IMG =
  "https://images.unsplash.com/photo-1639322537228-f710d846310a?auto=format&fit=crop&w=1600&q=80";
const TEAM_IMG =
  "https://images.unsplash.com/photo-1633457897190-8c8c23a12c4c?auto=format&fit=crop&w=1600&q=80";

const SERVICES = [
  {
    id: "itmon",
    icon: Gauge,
    name: "IT Infrastructure Monitoring",
    title: "IT Infrastructure Monitoring",
    desc: "Monitor the IT infrastructure that matters most to your business with flexible, lightweight and highly customisable monitoring.",
    span: "md:col-span-7",
    href: "/it-infrastructure-monitoring",
    cta: "Explore IT Infrastructure Monitoring",
    tag: "NEW",
  },
  {
    id: "infra",
    icon: ServerCog,
    name: "Infrastructure Monitoring",
    title: "Infrastructure Monitoring & Support",
    desc: "24/7 monitoring and proactive support across servers, cloud workloads, databases, and network infrastructure — keeping your digital backbone healthy and incident-ready.",
    span: "md:col-span-5",
  },
  {
    id: "vapt",
    icon: ShieldCheck,
    name: "VAPT",
    title: "Vulnerability Assessment & Penetration Testing",
    desc: "Adversary-grade testing with audit-ready reports. We map every attack path before threat actors do.",
    span: "md:col-span-7",
  },
  {
    id: "threat",
    icon: Activity,
    name: "Threat Protection",
    title: "Real-time Threat Detection & Response",
    desc: "Continuous detection, triage, and containment by L2 SOC analysts with playbook-driven response.",
    span: "md:col-span-5",
  },
  {
    id: "network",
    icon: Network,
    name: "Network Monitoring",
    title: "Network Device Monitoring",
    desc: "Deep telemetry across firewalls, switches, and endpoints with anomaly correlation and instant alerts.",
    span: "md:col-span-5",
  },
  {
    id: "e2e",
    icon: Eye,
    name: "E2E Security Monitoring",
    title: "End-to-End Security Monitoring Services",
    desc: "Single pane-of-glass coverage from cloud to endpoint, fully managed by Quasect's L2 SOC.",
    span: "md:col-span-7",
  },
  {
    id: "whitelabel",
    icon: Tag,
    name: "White Label Solution",
    title: "White Label Solution",
    desc: "We provide White Label solutions for clients, enabling businesses to offer advanced cybersecurity monitoring, threat detection, incident response, and managed security services under their own brand.",
    span: "md:col-span-12",
  },
];

const FEATURES = [
  {
    icon: Clock,
    title: "24/7 SOC Monitoring",
    body: "Round-the-clock surveillance by certified L2 analysts. No blind windows. No surprises.",
    metric: "247 / 365",
  },
  {
    icon: AlertTriangle,
    title: "Real-time Alerts & Incident Response",
    body: "Mean-time-to-detect under 60 seconds. Mean-time-to-respond measured in minutes, not hours.",
    metric: "MTTD < 60s",
  },
  {
    icon: Brain,
    title: "AI-Driven Threat Intelligence",
    body: "Machine-learning anomaly detection that learns your environment and surfaces what matters.",
    metric: "COMING SOON",
    badge: true,
  },
  {
    icon: FileCheck2,
    title: "Compliance-Ready Reporting",
    body: "Audit trails for ISO 27001, SOC 2, PCI-DSS, HIPAA, GDPR — generated automatically.",
    metric: "5 FRAMEWORKS",
  },
];

const REASONS = [
  {
    n: "01",
    title: "Expertise-Driven Approach",
    body: "Founded by operators with 40+ years combined experience in enterprise cybersecurity.",
  },
  {
    n: "02",
    title: "Enterprise-Grade Tools",
    body: "Best-in-class SIEM, SOAR, EDR, and XDR — orchestrated by humans who know how to use them.",
  },
  {
    n: "03",
    title: "Fast Incident Response",
    body: "Sub-minute detection. Documented playbooks. Containment before lateral movement.",
  },
  {
    n: "04",
    title: "Scalable Security Solutions",
    body: "From a single subsidiary to global, multi-cloud estates — coverage scales with your growth.",
  },
];

const CLIENT_LOGOS = [
  { name: "REMIGES", url: "https://remiges.tech/" },
  { name: "SS NOVATECH", url: "https://www.ssnovatech.com.au/" },
  { name: "—" },
  { name: "—" },
  { name: "—" },
  { name: "—" },
  { name: "—" },
  { name: "—" },
];

const TESTIMONIALS = [
  {
    quote:
      "Quasect's L2 SOC contained a zero-day exploitation attempt before our IR team could even page on-call. That alone paid for the contract twice over.",
    author: "Director, Information Security",
    company: "Global Manufacturing — Fortune 500",
  },
  {
    quote:
      "The VAPT report wasn't a checklist — it was an actual threat model. Their analysts walked our board through findings in plain English.",
    author: "Chief Risk Officer",
    company: "Tier-1 Indian Bank",
  },
  {
    quote:
      "MTTD dropped from 4 hours to under 60 seconds in 90 days. The compliance reporting alone saved us a quarter of audit prep.",
    author: "VP, Engineering",
    company: "SaaS Unicorn",
  },
];

const POSTS = [
  {
    tag: "Threat Intel",
    title: "Anatomy of a Modern Supply Chain Attack",
    date: "Coming Soon",
  },
  {
    tag: "VAPT",
    title: "Why Annual Pen Tests Aren't Enough Anymore",
    date: "Coming Soon",
  },
  {
    tag: "Compliance",
    title: "Building a Continuous SOC 2 Evidence Pipeline",
    date: "Coming Soon",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-[#050507] text-white">
      <SEO
        title="Quasect — SOC Level 2 Cybersecurity, VAPT & 24/7 Threat Monitoring"
        description="Next-gen SOC Level 2 security for modern enterprises. VAPT, real-time threat protection, network device monitoring, and end-to-end security monitoring."
        path="/"
      />

      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden noise">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#050507] via-[#08080B] to-[#050507]" />
          <div className="absolute inset-0 bg-grid bg-grid-fade opacity-60" />
          {/* Radial accent glow — no stock imagery */}
          <div
            className="absolute -top-40 -right-40 w-[800px] h-[800px] rounded-full opacity-20 blur-3xl"
            style={{ background: "radial-gradient(circle, #00F0FF 0%, transparent 70%)" }}
          />
          <div
            className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full opacity-15 blur-3xl"
            style={{ background: "radial-gradient(circle, #39FF14 0%, transparent 70%)" }}
          />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-24 pb-20 md:pt-32 md:pb-28">
          {/* announcement banner */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="hero-announcement"
            className="inline-flex items-center gap-3 bg-[#00F0FF]/10 border border-[#00F0FF]/30 px-4 py-2 font-mono text-[11px] tracking-widest text-[#00F0FF] uppercase"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Coming Soon: AI-Enabled Auto Resolve Functionality
            <span className="w-1.5 h-1.5 bg-[#39FF14] pulse-dot ml-1" />
          </motion.div>

          <div className="grid lg:grid-cols-12 gap-10 mt-8 items-start">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="font-mono text-xs tracking-[0.3em] uppercase text-[#39FF14] flex items-center gap-3"
              >
                <span className="w-6 h-px bg-[#39FF14]" />
                RMM · ENDPOINT MANAGEMENT / COMPLIANCE MANAGEMENT
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.05 }}
                data-testid="hero-headline"
                className="font-display font-black mt-6 text-4xl sm:text-5xl md:text-5xl lg:text-[58px] tracking-tighter leading-[1.05] break-words"
              >
                Next-Gen <span className="text-[#39FF14]">Secure IT Infrastructure Monitoring</span>
                <br />
                for Modern{" "}
                <span className="shimmer-text">Enterprises.</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.7 }}
                className="mt-7 text-lg md:text-xl text-[#8B93A5] leading-relaxed max-w-2xl"
              >
                <span className="text-white">Lightweight, highly customisable monitoring</span>{" "}
                for servers, databases, cloud &amp; enterprise services — paired with proactive
                VAPT and real-time threat protection. Engineered by operators with{" "}
                <span className="text-white">40+ years</span> of combined frontline experience.
              </motion.p>

              {/* IT Infrastructure Monitoring highlights */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.28, duration: 0.7 }}
                data-testid="infra-highlights"
                className="mt-6 flex flex-wrap gap-2"
              >
                {[
                  "Choose what to monitor",
                  "Custom thresholds & intervals",
                  "Priority-based alerting",
                  "Servers · DBs · Cloud · Apps",
                ].map((h) => (
                  <span
                    key={h}
                    className="inline-flex items-center gap-2 border border-[#39FF14]/30 bg-[#39FF14]/[0.04] text-[#39FF14] font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-1.5"
                  >
                    <span className="w-1 h-1 bg-[#39FF14]" />
                    {h}
                  </span>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-9 flex flex-wrap gap-4"
              >
                <Link
                  to="/contact"
                  data-testid="hero-cta-protected"
                  className="btn-sweep bg-[#39FF14] text-black font-bold uppercase tracking-wider px-7 py-4 text-sm hover:bg-[#32E612] transition-colors flex items-center gap-2"
                >
                  Get Protected <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-cta-demo"
                  className="bg-transparent border border-white/30 hover:border-white text-white font-medium px-7 py-4 text-sm uppercase tracking-wider transition-all flex items-center gap-2"
                >
                  Request Demo
                </Link>
              </motion.div>

              <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl">
                {[
                  { v: "40+", l: "Years combined experience" },
                  { v: "<30s", l: "Mean time for alerting" },
                  { v: "24/7", l: "Coverage" },
                ].map((s) => (
                  <div key={s.l} className="border-l border-white/10 pl-4">
                    <div className="font-display font-bold text-2xl text-white">{s.v}</div>
                    <div className="font-mono text-[10px] text-[#8B93A5] uppercase tracking-wider mt-1">
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.8 }}
              className="lg:col-span-5"
            >
              <NetworkVisual />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CLIENT LOGOS MARQUEE */}
      <section data-testid="clients-section" className="border-y border-white/10 py-8 bg-[#050507]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-5 flex items-center gap-3">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#8B93A5]">
            Trusted by teams / MSPs
          </span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="marquee-mask">
          <Marquee speed={40} gradient={false} pauseOnHover>
            {CLIENT_LOGOS.concat(CLIENT_LOGOS).map((c, i) =>
              c.url ? (
                <a
                  key={i}
                  href={c.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`client-logo-${i}`}
                  className="mx-12 font-display font-black text-3xl tracking-tighter text-white/40 hover:text-[#39FF14] transition-colors"
                >
                  {c.name}
                </a>
              ) : (
                <div
                  key={i}
                  className="mx-12 font-display font-black text-3xl tracking-tighter text-white/15"
                  data-testid={`client-logo-${i}`}
                >
                  {c.name}
                </div>
              )
            )}
          </Marquee>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section data-testid="services-section" className="py-24 md:py-32 relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader
            overline="Services / Capabilities"
            title="IT Infrastructure Monitoring & Security stack engineered for adversary-grade threats."
            subtitle="Pillars of enterprise IT infrastructure monitoring and cybersecurity, delivered as managed services by Quasect's team."
            testId="services-header"
          />

          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.05, duration: 0.6 }}
                className={`tracing-card group relative bg-[#0A0A0D] border border-white/10 hover:border-[#00F0FF]/50 hover:bg-[#0A0A0D]/80 p-7 md:p-9 transition-all ${s.span}`}
                data-testid={`service-card-${s.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 border border-white/15 flex items-center justify-center group-hover:border-[#39FF14] group-hover:bg-[#39FF14]/10 transition-all">
                    <s.icon className="w-5 h-5 text-[#00F0FF] group-hover:text-[#39FF14] transition-colors" />
                  </div>
                  <div className="flex items-center gap-2">
                    {s.tag && (
                      <span className="font-mono text-[9px] tracking-[0.2em] uppercase border border-[#39FF14]/40 text-[#39FF14] px-2 py-0.5 bg-[#39FF14]/5">
                        {s.tag}
                      </span>
                    )}
                    <span className="font-mono text-[10px] tracking-widest text-[#8B93A5] uppercase">
                      0{i + 1} / 07
                    </span>
                  </div>
                </div>
                <div className="mt-8 font-mono text-xs tracking-[0.2em] uppercase text-[#00F0FF]">
                  {s.name}
                </div>
                <h3 className="font-display font-bold text-2xl md:text-3xl mt-3 leading-tight tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-[#8B93A5] leading-relaxed">{s.desc}</p>
                {s.href ? (
                  <Link
                    to={s.href}
                    data-testid={`service-card-${s.id}-cta`}
                    className="mt-7 inline-flex items-center gap-2 text-sm uppercase tracking-wider text-[#39FF14] hover:gap-3 transition-all"
                  >
                    {s.cta || "Learn more"} <ArrowUpRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <div className="mt-7 flex items-center gap-2 text-sm uppercase tracking-wider text-white/70 group-hover:text-[#39FF14] transition-colors">
                    Learn more <ArrowUpRight className="w-4 h-4" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section data-testid="features-section" className="py-24 md:py-32 border-t border-white/10 bg-[#08080A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader
            overline="Capabilities"
            title="Operational depth, measured in milliseconds."
            subtitle="Every capability is monitored, measured, and continuously improved by our team."
            testId="features-header"
          />

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.05 }}
                className="tracing-card group bg-[#0A0A0D] border border-white/10 hover:border-[#00F0FF]/50 p-7 md:p-9 flex items-start gap-5 transition-all"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 shrink-0 border border-white/15 flex items-center justify-center group-hover:border-[#39FF14] transition-all">
                  <f.icon className="w-5 h-5 text-[#00F0FF] group-hover:text-[#39FF14] transition-colors" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display font-bold text-xl md:text-2xl">{f.title}</h3>
                    {f.badge && (
                      <span className="font-mono text-[9px] tracking-[0.2em] uppercase border border-[#39FF14]/40 text-[#39FF14] px-2 py-0.5 bg-[#39FF14]/5">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="mt-3 text-[#8B93A5] leading-relaxed">{f.body}</p>
                  <div className="mt-5 font-mono text-xs tracking-[0.2em] uppercase text-[#00F0FF]">
                    {f.metric}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section data-testid="why-section" className="py-24 md:py-32 relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <div className="font-mono text-xs tracking-[0.25em] uppercase text-[#00F0FF] mb-4 flex items-center gap-3">
              <span className="w-6 h-px bg-[#00F0FF]" /> Why Quasect
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-bold leading-[1.05]">
              Built by operators.
              <br />
              <span className="text-[#39FF14]">Trusted by CISOs.</span>
            </h2>
            <p className="mt-5 text-[#8B93A5] leading-relaxed">
              Cybersecurity is not a product. It's a discipline. Quasect was founded to bring
              real operator-grade defense to organizations that can't afford to lose.
            </p>

            <div className="mt-10 border border-white/10 bg-[#0A0A0D] p-6 relative noise">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8B93A5] mb-3">
                Compliance Ready
              </div>
              <div className="flex flex-wrap gap-2">
                {["ISO 27001", "SOC 2", "PCI-DSS", "HIPAA", "GDPR", "NIST"].map((c) => (
                  <span
                    key={c}
                    className="font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 border border-white/15 text-white/80"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-4">
            {REASONS.map((r, i) => (
              <motion.div
                key={r.n}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.07 }}
                className="tracing-card group bg-[#0A0A0D] border border-white/10 hover:border-[#39FF14]/50 p-7 md:p-9 flex gap-6 transition-all"
                data-testid={`reason-card-${i}`}
              >
                <div className="font-display font-black text-3xl md:text-4xl text-[#39FF14]/30 group-hover:text-[#39FF14] transition-colors">
                  {r.n}
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl md:text-2xl">{r.title}</h3>
                  <p className="mt-3 text-[#8B93A5] leading-relaxed">{r.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VISUAL SECTION */}
      <section
        data-testid="visual-section"
        className="py-24 md:py-32 border-y border-white/10 bg-[#08080A]"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-6">
              <SectionHeader
                overline="Inside the SOC"
                title="One pane of glass. Zero blind spots."
                subtitle="Every alert correlated. Every anomaly triaged. Every incident contained — by humans who own the outcome."
                testId="visual-header"
              />
              <div className="mt-8 space-y-3">
                {[
                  "Sub-minute MTTD across enterprise estates",
                  "Tier-2 analyst eyes on every High/Critical alert",
                  "Audit-ready evidence chains, generated continuously",
                  "Plays integrated with your existing SIEM, EDR, and ticketing",
                ].map((b) => (
                  <div key={b} className="flex items-start gap-3 text-white/85">
                    <CheckCircle2 className="w-5 h-5 text-[#39FF14] shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{b}</span>
                  </div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-6 grid grid-cols-2 gap-4"
            >
              <div className="col-span-2 relative border border-white/10 bg-[#0A0A0D] overflow-hidden aspect-[16/9]">
                <img
                  src={VISUAL_IMG}
                  alt="Abstract network data visualization"
                  className="w-full h-full object-cover opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#050507] via-transparent to-[#00F0FF]/10" />
                <div className="absolute bottom-3 left-3 font-mono text-[10px] tracking-widest text-[#00F0FF]">
                  E2E TELEMETRY · ENC TLS-1.3
                </div>
              </div>
              <div className="relative border border-white/10 bg-[#0A0A0D] overflow-hidden aspect-square">
                <img
                  src={TEAM_IMG}
                  alt="Quasect SOC analysts in operations meeting"
                  className="w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050507] via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 font-mono text-[10px] tracking-widest text-[#39FF14]">
                  L2 SOC · ON-CALL
                </div>
              </div>
              <div className="border border-white/10 bg-[#0A0A0D] p-5 flex flex-col justify-between aspect-square">
                <div className="flex items-center justify-between">
                  <Cpu className="w-5 h-5 text-[#00F0FF]" />
                  <span className="font-mono text-[10px] tracking-widest text-[#8B93A5]">
                    AI-INTEL
                  </span>
                </div>
                <div>
                  <div className="font-display font-black text-3xl">98.7%</div>
                  <div className="font-mono text-[10px] tracking-widest text-[#8B93A5] uppercase mt-1">
                    Detection precision
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader
            overline="Operator Feedback"
            title="The L2 SOC that CISOs ask for by name."
            testId="testimonials-header"
          />
          <div className="mt-14 grid md:grid-cols-3 gap-4 md:gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.07 }}
                className="bg-[#0A0A0D] border border-white/10 p-7 md:p-8 hover:border-[#00F0FF]/40 transition-colors"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="w-7 h-7 text-[#39FF14]" />
                <p className="mt-5 text-white/90 leading-relaxed">{t.quote}</p>
                <div className="mt-7 pt-5 border-t border-white/10">
                  <div className="font-display font-bold text-sm">{t.author}</div>
                  <div className="font-mono text-[11px] text-[#8B93A5] uppercase tracking-wider mt-1">
                    {t.company}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG */}
      <section data-testid="blog-section" className="py-24 md:py-32 border-t border-white/10 bg-[#08080A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <SectionHeader
              overline="Insights / Newsroom"
              title="Field notes from the SOC."
              testId="blog-header"
            />
            <Link
              to="/blog"
              data-testid="blog-view-all"
              className="text-sm uppercase tracking-wider border border-white/20 hover:border-white px-4 py-2.5 transition-colors flex items-center gap-2"
            >
              View All <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-4 md:gap-6">
            {POSTS.map((p, i) => (
              <article
                key={i}
                data-testid={`blog-card-${i}`}
                className="tracing-card group bg-[#0A0A0D] border border-white/10 hover:border-[#39FF14]/40 p-7 md:p-8 transition-all noise relative"
              >
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF]">
                  {p.tag}
                </div>
                <h3 className="font-display font-bold text-xl md:text-2xl mt-4 leading-tight tracking-tight">
                  {p.title}
                </h3>
                <div className="mt-8 pt-5 border-t border-white/10 flex items-center justify-between font-mono text-[11px] uppercase tracking-wider text-[#8B93A5]">
                  <span>{p.date}</span>
                  <span className="group-hover:text-[#39FF14] transition-colors">READ →</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="cta-section" className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
          <div className="border border-white/10 bg-[#0A0A0D] p-10 md:p-16 relative overflow-hidden noise">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#39FF14] to-transparent" />
            <div className="grid lg:grid-cols-12 gap-10 items-center relative">
              <div className="lg:col-span-8">
                <div className="font-mono text-xs tracking-[0.25em] uppercase text-[#00F0FF] flex items-center gap-3">
                  <Zap className="w-3.5 h-3.5" /> READY WHEN YOU ARE
                </div>
                <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-5 leading-[1.05]">
                  Secure your business <span className="text-[#39FF14]">today.</span>
                </h2>
                <p className="mt-5 text-[#8B93A5] text-lg max-w-2xl">
                  Talk to a Quasect L2 analyst within 24 hours. No sales theatre — just a real
                  scoping conversation about your environment.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Link
                  to="/contact"
                  data-testid="final-cta-secure"
                  className="btn-sweep bg-[#39FF14] text-black font-bold uppercase tracking-wider px-7 py-4 text-sm hover:bg-[#32E612] transition-colors flex items-center justify-center gap-2"
                >
                  Secure Your Business <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a
                  href="mailto:info@quasect.com"
                  data-testid="final-cta-email"
                  className="border border-white/20 hover:border-white text-white px-7 py-4 text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2"
                >
                  <LockKeyhole className="w-4 h-4" /> info@quasect.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
